import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editValues, setEditValues] = useState({});

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get("/settings");
      setSettings(res.data);
      const vals = {};
      res.data.forEach((s) => { vals[s.key] = s.value; });
      setEditValues(vals);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSetting = async (key) => {
    setSaving(key);
    try {
      await api.put("/settings", { key, value: parseFloat(editValues[key]) || 0 });
      toast.success("Setting updated");
      fetchSettings();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(null);
    }
  };

  const isAdmin = user?.role === "admin";

  const settingLabels = {
    default_crimp: { label: "Default Crimp (%)", description: "Percentage added to warp weight calculation for crimp factor." },
    warp_ends_offset: { label: "Warp Ends Offset", description: "Constant offset added to warp ends before rounding to nearest 50." },
    weaver_profit_margin: { label: "Weaver Profit Margin (%)", description: "Profit percentage added on overall cost to calculate Greige Cost per Dhoti." },
    warehouse_expenses: { label: "Warehouse Expenses (%)", description: "Percentage added on top of final cost (Greige + Processing + Folding) for warehouse expenses." },
    chairman_pin: { label: "Chairman View PIN", description: "6-digit PIN for chairman read-only access. Share the link: /view" },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Settings
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Configure calculation constants. {!isAdmin && "Only admins can modify settings."}
        </p>
      </div>

      <div className="space-y-4 max-w-lg">
        {settings.map((s) => {
          const meta = settingLabels[s.key] || { label: s.key, description: s.description || "" };
          return (
            <div
              key={s.key}
              className="bg-white border border-stone-200 rounded-lg p-5"
              data-testid={`setting-${s.key}`}
            >
              <Label className="text-sm font-medium text-stone-900 block mb-1">
                {meta.label}
              </Label>
              <p className="text-xs text-stone-500 mb-3">{meta.description}</p>
              <div className="flex gap-3 items-center">
                <Input
                  type="number"
                  step="0.01"
                  data-testid={`setting-input-${s.key}`}
                  value={editValues[s.key] ?? ""}
                  onChange={(e) => setEditValues((v) => ({ ...v, [s.key]: e.target.value }))}
                  className="max-w-[200px]"
                  disabled={!isAdmin}
                />
                {isAdmin && (
                  <Button
                    size="sm"
                    data-testid={`save-setting-${s.key}`}
                    onClick={() => saveSetting(s.key)}
                    disabled={saving === s.key}
                    className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
                  >
                    {saving === s.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                )}
              </div>
              {s.updated_at && (
                <p className="text-xs text-stone-400 mt-2">
                  Last updated: {new Date(s.updated_at).toLocaleDateString("en-IN")}
                </p>
              )}
            </div>
          );
        })}

        {settings.length === 0 && (
          <p className="text-sm text-stone-400">No settings configured yet.</p>
        )}
      </div>
    </div>
  );
}
